// ═══════════════════════════════════════════════════════════════════════════
// Navigator 360 — Career Definitions & Mapping Data
// Based on Section 7.5 of the Navigator 360 Technical Specification v1.0
// ═══════════════════════════════════════════════════════════════════════════

import { CareerDefinition, RIASECType } from './Navigator360Types';

export const CAREER_DEFINITIONS: CareerDefinition[] = [
  {
    id: 'cs-ai',
    name: 'CS & AI',
    riasec: ['I', 'R', 'C'] as RIASECType[],
    mi: ['Logical-Mathematical', 'Intrapersonal', 'Visual-Spatial'],
    abilities: ['Logical reasoning', 'Computational', 'Technical'],
    values: ['Mental Activity', 'Autonomy', 'Good Salary', 'High Achievement'],
    degreePaths: ['B.Tech CS/IT', 'B.Sc Computer Science', 'BCA', 'M.Tech AI/ML'],
  },
  {
    id: 'engineering',
    name: 'Engineering & Tech',
    riasec: ['R', 'I', 'C'] as RIASECType[],
    mi: ['Logical-Mathematical', 'Visual-Spatial', 'Bodily-Kinesthetic'],
    abilities: ['Technical', 'Computational', 'Form perception'],
    values: ['Good Salary', 'High Achievement', 'Autonomy', 'Working with Hands'],
    degreePaths: ['B.Tech/B.E.', 'Diploma Engineering', 'M.Tech'],
  },
  {
    id: 'science-maths',
    name: 'Science & Maths',
    riasec: ['I', 'R'] as RIASECType[],
    mi: ['Logical-Mathematical', 'Intrapersonal', 'Naturalistic'],
    abilities: ['Logical reasoning', 'Computational', 'Speed and accuracy'],
    values: ['Mental Activity', 'Autonomy', 'High Achievement'],
    degreePaths: ['B.Sc', 'Integrated M.Sc', 'Ph.D'],
  },
  {
    id: 'life-sciences-medicine',
    name: 'Life Sciences / Medicine',
    riasec: ['I', 'S'] as RIASECType[],
    mi: ['Bodily-Kinesthetic', 'Intrapersonal', 'Interpersonal'],
    abilities: ['Decision making & problem solving', 'Finger dexterity', 'Logical reasoning'],
    values: ['Helping Others', 'Prestige', 'Good Salary', 'Mental Activity'],
    degreePaths: ['MBBS', 'B.Sc Nursing', 'BDS', 'B.Pharm', 'BAMS/BHMS'],
  },
  {
    id: 'architecture-design',
    name: 'Architecture & Design',
    riasec: ['A', 'R', 'I'] as RIASECType[],
    mi: ['Visual-Spatial', 'Logical-Mathematical', 'Bodily-Kinesthetic'],
    abilities: ['Form perception', 'Technical', 'Creativity/Artistic'],
    values: ['Creativity', 'Autonomy', 'Prestige', 'Working with Hands'],
    degreePaths: ['B.Arch', 'B.Des', 'BFA', 'M.Des'],
  },
  {
    id: 'media-communication',
    name: 'Media & Communication',
    riasec: ['A', 'E', 'S'] as RIASECType[],
    mi: ['Linguistic', 'Interpersonal', 'Musical'],
    abilities: ['Language/Communication', 'Creativity/Artistic', 'Speed and accuracy'],
    values: ['Creativity', 'Variety', 'Social Impact', 'High Achievement'],
    degreePaths: ['BA Journalism', 'BMM', 'B.Sc Film/TV', 'MA Communication'],
  },
  {
    id: 'education-teaching',
    name: 'Education & Teaching',
    riasec: ['S', 'A', 'I'] as RIASECType[],
    mi: ['Interpersonal', 'Linguistic', 'Intrapersonal'],
    abilities: ['Language/Communication', 'Logical reasoning', 'Decision making & problem solving'],
    values: ['Helping Others', 'Social Impact', 'Job Security', 'Work-Life Balance'],
    degreePaths: ['B.Ed', 'BA Education', 'M.Ed', 'D.El.Ed'],
  },
  {
    id: 'law',
    name: 'Law',
    riasec: ['E', 'I', 'S'] as RIASECType[],
    mi: ['Linguistic', 'Intrapersonal', 'Interpersonal'],
    abilities: ['Logical reasoning', 'Language/Communication', 'Decision making & problem solving'],
    values: ['Prestige', 'Mental Activity', 'Good Salary', 'High Achievement'],
    degreePaths: ['BA LLB', 'BBA LLB', 'LLB', 'LLM'],
  },
  {
    id: 'finance-banking',
    name: 'Finance & Banking',
    riasec: ['C', 'E', 'I'] as RIASECType[],
    mi: ['Logical-Mathematical', 'Intrapersonal'],
    abilities: ['Computational', 'Speed and accuracy', 'Decision making & problem solving'],
    values: ['Good Salary', 'Job Security', 'High Achievement', 'Routine Activity'],
    degreePaths: ['B.Com', 'BBA Finance', 'CA', 'CFA', 'MBA Finance'],
  },
  {
    id: 'management-entrepreneurship',
    name: 'Management & Entrepreneurship',
    riasec: ['E', 'I', 'S'] as RIASECType[],
    mi: ['Interpersonal', 'Intrapersonal', 'Linguistic'],
    abilities: ['Decision making & problem solving', 'Language/Communication', 'Logical reasoning'],
    values: ['Leadership', 'High Achievement', 'Autonomy', 'Good Salary'],
    degreePaths: ['BBA', 'MBA', 'PGDM', 'Entrepreneurship Programs'],
  },
  {
    id: 'healthcare-allied',
    name: 'Healthcare (Allied)',
    riasec: ['S', 'R'] as RIASECType[],
    mi: ['Bodily-Kinesthetic', 'Interpersonal', 'Naturalistic'],
    abilities: ['Finger dexterity', 'Decision making & problem solving', 'Speed and accuracy'],
    values: ['Helping Others', 'Job Security', 'Variety', 'Physical Activity'],
    degreePaths: ['B.Sc Physiotherapy', 'B.Sc MLT', 'B.Sc Radiology', 'BOT'],
  },
  {
    id: 'agriculture-environment',
    name: 'Agriculture & Environment',
    riasec: ['R', 'I', 'S'] as RIASECType[],
    mi: ['Naturalistic', 'Bodily-Kinesthetic', 'Visual-Spatial'],
    abilities: ['Technical', 'Decision making & problem solving', 'Form perception'],
    values: ['Physical Activity', 'Social Impact', 'Variety', 'Working with Hands'],
    degreePaths: ['B.Sc Agriculture', 'B.Tech Agri Engg', 'B.Sc Forestry', 'M.Sc Environmental Science'],
  },
  {
    id: 'creative-arts',
    name: 'Creative Arts & Performing',
    riasec: ['A', 'S'] as RIASECType[],
    mi: ['Musical', 'Bodily-Kinesthetic', 'Visual-Spatial'],
    abilities: ['Creativity/Artistic', 'Language/Communication', 'Form perception'],
    values: ['Creativity', 'Variety', 'Autonomy', 'Social Impact'],
    degreePaths: ['BFA', 'BA Performing Arts', 'B.Sc Animation', 'BA Music'],
  },
  {
    id: 'government-public',
    name: 'Government & Public Service',
    riasec: ['C', 'E', 'S'] as RIASECType[],
    mi: ['Interpersonal', 'Linguistic'],
    abilities: ['Language/Communication', 'Decision making & problem solving', 'Speed and accuracy'],
    values: ['Social Impact', 'Job Security', 'Prestige', 'Leadership'],
    degreePaths: ['BA Political Science', 'BA Public Administration', 'UPSC Preparation', 'LLB'],
  },
  {
    id: 'sports-physical',
    name: 'Sports & Physical Ed',
    riasec: ['R', 'S'] as RIASECType[],
    mi: ['Bodily-Kinesthetic', 'Interpersonal', 'Naturalistic'],
    abilities: ['Motor movement', 'Speed and accuracy', 'Decision making & problem solving'],
    values: ['Physical Activity', 'High Achievement', 'Leadership', 'Variety'],
    degreePaths: ['B.P.Ed', 'B.Sc Sports Science', 'Sports Management', 'Coaching Certifications'],
  },
];

// ── Value Labels mapped to spec values ──
// Backend value labels → spec value names
export const VALUE_LABEL_TO_SPEC: Record<string, string> = {
  'Lucrative Salary': 'Good Salary',
  'Job Security': 'Job Security',
  'Variety and Diversity': 'Variety',
  'Building Relations': 'Helping Others',
  'High achievement': 'High Achievement',
  'Autonomy': 'Autonomy',
  'Hands on activities': 'Working with Hands',
  'Prestige/Recognition': 'Prestige',
  'Creativity': 'Creativity',
  'Mental Activity': 'Mental Activity',
  'Physical Activity': 'Physical Activity',
  'Leadership': 'Leadership',
  'Routine Activity': 'Routine Activity',
  'Supervised Work': 'Work-Life Balance',
  'Working Conditions': 'Social Impact',
};

// ── Value Conflicts (Section 7.3) ──
export const VALUE_CONFLICTS: Record<string, string[]> = {
  'Job Security': ['Entrepreneurship', 'Creative Arts & Performing'],
  'Routine Activity': ['Creative Arts & Performing', 'Media & Communication', 'Science & Maths'],
  'Physical Activity': ['Finance & Banking', 'CS & AI'],
};

// ── Career Aspiration to career ID mapping ──
export const ASPIRATION_TO_CAREER: Record<string, string> = {
  'Architecture': 'architecture-design',
  'Art Design': 'creative-arts',
  'Entertainment and Mass Media': 'media-communication',
  'Management and Administration': 'management-entrepreneurship',
  'Banking and Finance': 'finance-banking',
  'Law Studies': 'law',
  'Government and Public Administration': 'government-public',
  'Marketing': 'management-entrepreneurship',
  'Entrepreneurship': 'management-entrepreneurship',
  'Sales': 'management-entrepreneurship',
  'Science and Mathematics': 'science-maths',
  'Computer Science IT and Allied Fields': 'cs-ai',
  'Life Sciences/Medicine and Healthcare': 'life-sciences-medicine',
  'Environmental Service': 'agriculture-environment',
  'Social Sciences and Humanities': 'education-teaching',
  'Defence/Protective Service': 'government-public',
  'Sports': 'sports-physical',
  'Engineering and Technology': 'engineering',
  'Agriculture Food Industry and Forestry': 'agriculture-environment',
  'Education and Training': 'education-teaching',
  'Paramedical': 'healthcare-allied',
  'Hospitality and Tourism': 'management-entrepreneurship',
  'Community and Social Service': 'education-teaching',
  'Personal Care and Services': 'healthcare-allied',
};

// ── Aspiration to RIASEC mapping (for coherence calculation) ──
export const ASPIRATION_RIASEC: Record<string, RIASECType> = {
  'Architecture': 'A',
  'Art Design': 'A',
  'Entertainment and Mass Media': 'A',
  'Management and Administration': 'E',
  'Banking and Finance': 'C',
  'Law Studies': 'E',
  'Government and Public Administration': 'C',
  'Marketing': 'E',
  'Entrepreneurship': 'E',
  'Sales': 'E',
  'Science and Mathematics': 'I',
  'Computer Science IT and Allied Fields': 'I',
  'Life Sciences/Medicine and Healthcare': 'I',
  'Environmental Service': 'R',
  'Social Sciences and Humanities': 'S',
  'Defence/Protective Service': 'R',
  'Sports': 'R',
  'Engineering and Technology': 'R',
  'Agriculture Food Industry and Forestry': 'R',
  'Education and Training': 'S',
  'Paramedical': 'S',
  'Hospitality and Tourism': 'E',
  'Community and Social Service': 'S',
  'Personal Care and Services': 'S',
};

// ── Subject to RIASEC mapping (for P4 calculation) ──
export const SUBJECT_RIASEC: Record<string, RIASECType> = {
  'Agriculture': 'R',
  'Art': 'A',
  'Cultural Studies': 'S',
  'English': 'A',
  'Home and Consumer Science': 'R',
  'Finance': 'C',
  'Health': 'S',
  'Languages': 'A',
  'Management': 'E',
  'Mathematics': 'I',
  'Music': 'A',
  'Science': 'I',
  'Vocational studies': 'R',
  'Social Sciences': 'S',
  'Technology': 'R',
};
